import { z } from "zod";
import { ChecklistOverdueAlertSettingsSchema } from "./alertSettingsRegistry.js";
export { ChecklistOverdueAlertSettingsSchema };
// ===== ENUM TYPES =====
export const TimeFormatSchema = z.enum(["12 hours", "24 hours"]);
export const HolidayManagementSchema = z.enum([
    "Rank Order",
    "bid system",
    "auto assign",
    "first come first serve",
]);
export const TimeOffAccrualPolicySchema = z.enum([
    "Per shift",
    "Per pay period",
    "Per week",
    "Per month",
]);
export const WeekendMaxHoursSchema = z.enum(["Custom", "Same as week day"]);
export const ApprovalPolicySchema = z.enum([
    "Yes",
    "No",
    "Autoapprove if conditions are met",
]);
export const ScheduleVisibilitySchema = z.enum([
    "All",
    "Shifts only",
    "Shifts and Job only",
    "No",
]);
export const PayPeriodLengthSchema = z.enum([
    "Weekly",
    "Every 2 weeks",
    "1st and 15th of each month",
    "Monthly",
]);
export const ReviewCycleFrequencySchema = z.enum([
    "Quarterly",
    "Twice a year",
    "Annual",
]);
export const BatchReviewFrequencySchema = z.enum([
    "Monthly",
    "Quarterly",
    "Yearly",
]);
export const TipDistributionUnsubmittedSchema = z.enum([
    "Defer distribution until next pay period",
    "Scheduled",
    "Time clock",
]);
// ===== TIMECARD APPROVAL MODE =====
export const TimecardApprovalMode = {
    APPROVER_GROUP: "APPROVER_GROUP",
    MANAGER: "MANAGER",
    BOTH: "BOTH",
};
// ===== CATEGORY SCHEMAS =====
export const GeneralSettingsSchema = z.object({
    timeFormat: TimeFormatSchema,
    timeZone: z.string(),
    calculateOtHoursAfterWeekly: z.number().int().nonnegative(),
    calculateDoubleOtHoursAfterWeekly: z.number().int().nonnegative().nullable(),
    calculateOtHoursAfterDaily: z.number().int().nonnegative().nullable(),
    calculateDoubleOtHoursAfterDaily: z.number().int().nonnegative().nullable(),
    workdayChangeover: z.string(), // time string like "12:00 AM"
    whoCanCreateDistrict: z.string(),
    whoCanCreateStore: z.array(z.string()),
    districtManagersPerDistrict: z.number().int().positive(),
    storeManagersPerDistrict: z.number().int().positive(),
    scheduleHoursBeforeOpen: z.number().int().nonnegative(),
    scheduleHoursAfterClose: z.number().int().nonnegative(),
});
export const TimeOffSettingsSchema = z.object({
    requireApprovalTeamMember: z.boolean(),
    requireApprovalSupervisor: z.boolean(),
    whoApprovesTimeOff: z.string(),
    showApprovedTimeOffToAll: z.boolean(),
    daysNoticeRequired: z.number().int().nonnegative(),
    holidayManagement: HolidayManagementSchema,
    maxPaidTimeOffHoursPerDay: z.number().int().nonnegative(),
    types: z.object({
        paidHoliday: z.boolean(),
        unpaidHoliday: z.boolean(),
        paidPersonalDays: z.boolean(),
        unpaidPersonalDays: z.boolean(),
        paidSickDays: z.boolean(),
        unpaidSickDays: z.boolean(),
    }),
    accrualPolicy: TimeOffAccrualPolicySchema,
    accrualRates: z.string(),
});
// ===== DAY PARTS (formerly Shift Intervals) =====
const timeFormatRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const DayPartSchema = z.object({
    name: z.string().min(1, "Day part name is required").max(50),
    startTime: z.string().regex(timeFormatRegex, "Time must be in HH:mm format"),
    endTime: z.string().regex(timeFormatRegex, "Time must be in HH:mm format"),
}).refine((data) => {
    const [sH, sM] = data.startTime.split(":").map(Number);
    const [eH, eM] = data.endTime.split(":").map(Number);
    return (sH * 60 + sM) < (eH * 60 + eM);
}, { message: "End time must be after start time" });
// Per-day array with duplicate name validation
const DayPartArraySchema = z.array(DayPartSchema).max(10).superRefine((intervals, ctx) => {
    const names = intervals.map((iv) => iv.name);
    const dupes = names.filter((n, i) => names.indexOf(n) !== i);
    if (dupes.length > 0) {
        ctx.addIssue({ code: "custom", message: `Duplicate day part name(s): ${[...new Set(dupes)].join(", ")}` });
    }
});
export const DayPartsSchema = z.object({
    MONDAY: DayPartArraySchema.optional(),
    TUESDAY: DayPartArraySchema.optional(),
    WEDNESDAY: DayPartArraySchema.optional(),
    THURSDAY: DayPartArraySchema.optional(),
    FRIDAY: DayPartArraySchema.optional(),
    SATURDAY: DayPartArraySchema.optional(),
    SUNDAY: DayPartArraySchema.optional(),
}).nullish();
// BACKWARD COMPAT aliases
export const ShiftIntervalSchema = DayPartSchema;
export const ShiftIntervalsSchema = DayPartsSchema;
// ===== SCHEDULING SETTINGS =====
export const SchedulingSettingsSchema = z.object({
    maxScheduledHoursWeek: z.number().int().nonnegative(),
    maxScheduledHoursDay: z.number().int().nonnegative(),
    allowWeeklyExceedWithApproval: z.boolean(),
    allowDailyExceedWithApproval: z.boolean(),
    notifyDmOtThresholdPercent: z.number().int().nonnegative(),
    weekendMaxHoursPerDay: WeekendMaxHoursSchema,
    allowBackToBackWeekends: z.boolean(),
    minHoursPerWeek: z.number().int().nonnegative(),
    allowSplitShifts: z.boolean(),
    minHoursBetweenShiftsSameDay: z.number().int().nonnegative(),
    minHoursBetweenShiftsMultiDay: z.number().int().nonnegative(),
    maxDaysPerWeek: z.number().int().positive().max(7),
    maxDaysScheduledInRow: z.number().int().positive(),
    allowShiftRelease: z.boolean(),
    allowShiftDropping: z.boolean(),
    shiftDropDaysBefore: z.number().int().nonnegative(),
    allowShiftSwapping: z.boolean(),
    shiftSwapDaysBefore: z.number().int().nonnegative(),
    requireManagerApprovalDrops: ApprovalPolicySchema,
    requireManagerApprovalSwaps: ApprovalPolicySchema,
    minShiftLength: z.number().int().positive(),
    maxShiftLength: z.number().int().positive(),
    minShiftLengthToSplit: z.number().int().positive(),
    minNoticeToChangeShiftHours: z.number().int().nonnegative(),
    maxOpeningsPerWeekPerMember: z.number().int().nonnegative(),
    maxClosingsPerWeekPerMember: z.number().int().nonnegative(),
    allowClopening: z.boolean(),
    requireShiftConfirmation: z.boolean(),
    advanceNoticeAvailabilityChangeDays: z.number().int().nonnegative(),
    requireManagerApprovalAvailability: z.boolean(),
    coworkerScheduleVisibility: ScheduleVisibilitySchema,
    coworkerBreakVisibility: ScheduleVisibilitySchema,
    coworkerInfoVisible: z.array(z.string()),
    notifyManagerOtWarning: z.boolean(),
    notifyManagerOtHoursThreshold: z.number().int().nonnegative(),
    enableManagerCostView: z.boolean(),
    includeSalariedInCosts: z.boolean(),
    additionalBenefitsPercent: z.number().int().nonnegative(),
    laborBudgetPercentOfSales: z.number().min(0).max(1).default(0.3),
    highlightNewHires: z.boolean(),
    unqualifiedJobPayMethod: z.string(),
    breakInScheduledHours: z.string(),
    includePtoInLaborCost: z.boolean(),
    alertUnevenHoursDistribution: z.boolean(),
    maxOpeningShiftsInRow: z.number().int().nonnegative(),
    maxClosingShiftsInRow: z.number().int().nonnegative(),
    notifyHoursFluctuation: z.boolean(),
    hoursFluctuationThresholdPercent: z.number().int().nonnegative(),
    setMinorThresholds: z.boolean(),
    minorThresholdRules: z.string(),
    shiftIntervals: ShiftIntervalsSchema,
});
export const ClockingSettingsSchema = z.object({
    clockInMethods: z.array(z.string()),
    requireGeofencing: z.boolean(),
    geofenceDistanceFt: z.number().int().positive(),
    requirePhotoSharedDevice: z.boolean(),
    earlyClockInMinutesEmployee: z.number().int().nonnegative(),
    earlyClockInMinutesSupervisor: z.number().int().nonnegative(),
    unplannedWorkPolicy: z.string(),
    whoCanClockOutEmployee: z.array(z.string()),
    notifyEmployeeBeforeClockIn: z.number().int().nonnegative().nullable(),
    notifyEmployeeAfterClockInMinutes: z.number().int().nonnegative(),
    notifyManagerAfterClockInMinutes: z.number().int().nonnegative(),
    notifyEmployeeAfterClockOutMinutes: z.number().int().nonnegative(),
    notifyManagerAfterClockOutMinutes: z.number().int().nonnegative(),
    roundClockInOut: z.number().int().nonnegative().nullable(),
    requireClockUnpaidBreaks: z.boolean(),
    requireClockPaidBreaks: z.boolean(),
    askBreakAttestationOnClockOut: z.boolean(),
    requireSalariedClockInOut: z.boolean(),
    requirePinSharedDevice: z.boolean(),
    pinLength: z.number().int().positive(),
    shiftChangeRequestThresholdMinutes: z.number().int().nonnegative(),
    supervisorJobRoleFamilyIds: z.array(z.uuid()).default([]),
});
export const PayrollProviderSchema = z.enum(["default", "rippling"]);
export const PaySettingsSchema = z.object({
    currentPayPeriodStart: z.string().nullable(), // date string
    payPeriodLength: PayPeriodLengthSchema,
    allowPayOnDemand: z.boolean(),
    payOnDemandEarliestRequest: z.string(),
    whoApprovesCompensationChanges: z.string(),
    payrollProvider: PayrollProviderSchema.default("default"),
});
export const ChecklistSettingsSchema = z.object({
    useChecklists: z.boolean(),
    allowPartialAssignment: z.boolean(),
});
export const TimeAndAttendanceSettingsSchema = z.object({
    notifyManagerHoursThreshold: z.number().int().nonnegative(),
    notifyManagerDaysWorkedThreshold: z.number().int().nonnegative(),
    notifyManagerInactiveDays: z.number().int().nonnegative(),
    notifyManagerBeforeAdditionalPay: z.boolean(),
    notifyManagerAfterAdditionalPay: z.boolean(),
    notifyManagerMissedBreaks: z.boolean(),
    notifyManagerUnplannedWork: z.boolean(),
    notifyManagerUnapprovedHours: z.boolean(),
    notifyManagerUnsubmittedHours: z.boolean(),
    requireTimecardSubmission: z.boolean(),
    requireTimecardAttestation: z.boolean(),
    requireManagerTimecardApproval: z.boolean(),
    allowTimecardAutoApproval: z.boolean(),
    autoApproveThresholdMinutes: z.number().int().nonnegative(),
    whoApprovesTimecards: z.preprocess((v) => v === "Location Manager" ? "APPROVER_GROUP" : v, z.enum(["APPROVER_GROUP", "MANAGER", "BOTH"])),
    backupTimecardApprovers: z.array(z.string()),
    timecardApprovalDeadlineDays: z.number().int().nonnegative(),
    timecardApprovalDeadlineTime: z.string(), // time string
    enableAttendancePoints: z.boolean(),
    oooApproverPolicy: z.array(z.string()),
});
// Penny rounding strategy for tip distribution
export const PennyRoundingStrategySchema = z.enum([
    "round_up", // Round each distribution up (current default behavior)
    "lowest_earner", // Give extra pennies to lowest earner(s)
    "random_person", // Randomly distribute extra pennies
]);
// How to handle timecards in bad state during tip distribution
export const TimecardStateHandlingSchema = z.enum([
    "block", // Block distribution if any timecards are in bad state
    "fallback_to_scheduled", // Use scheduled shift times instead of clock in/out
]);
export const TipSettingsSchema = z.object({
    requireManagerApprovalTips: z.boolean(),
    tipDistributionUnsubmitted: TipDistributionUnsubmittedSchema,
    // User-defined external tip source names (e.g., "DoorDash", "UberEats")
    // SQUARE and CASH_DECLARATION are built-in and always available
    externalTipSources: z
        .array(z.string().min(1).max(100))
        .max(50)
        .default([]),
    // Strategy for distributing leftover pennies from rounding
    pennyRoundingStrategy: PennyRoundingStrategySchema.default("lowest_earner"),
    // How to handle timecards that are open/in bad state
    timecardStateHandling: TimecardStateHandlingSchema.default("fallback_to_scheduled"),
});
export const CareerSettingsSchema = z.object({
    reviewCycleFrequency: ReviewCycleFrequencySchema,
    batchReviewCycles: z.boolean(),
    batchReviewFrequency: BatchReviewFrequencySchema,
    enableTriggeredPromotionReviews: z.boolean(),
});
export const WeekOccurrenceSchema = z.enum([
    "FIRST",
    "SECOND",
    "THIRD",
    "FOURTH",
    "LAST",
]);
// DayOfWeek type is already exported from employmentPreferences.ts
// We only need the Zod schema here for validation
export const DayOfWeekSchema = z.enum([
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
]);
export const ReportSettingsSchema = z.object({
    newReportNotification: z.array(z.string()),
    publicReportsAvailable: z.string(),
    reportDeliveryCadence: z.string(),
    // New fields for scheduled report delivery timing (company-wide)
    reportDeliveryTimeUtc: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .default("08:00"), // "HH:MM"
    reportDeliveryDayOfWeek: DayOfWeekSchema.default("MONDAY"), // For WEEKLY/BIWEEKLY/MONTHLY
    reportDeliveryWeekOccurrence: WeekOccurrenceSchema.default("FIRST"), // For MONTHLY: Nth weekday
});
export const HolidaysAndEventsSettingsSchema = z.object({
    whoCanAddEvent: z.array(z.string()),
    whoApprovesAddingEvent: z.string(),
});
export const AccessSettingsSchema = z.object({
    enableExternalPartners: z.boolean(),
});
// ===== SCHEDULE WARNING TOGGLES =====
/** Toggle schema for each of the 15 schedule warning types */
export const ScheduleWarningTogglesSchema = z.object({
    // Staffing
    overlappingShifts: z.boolean().default(true),
    insufficientLabor: z.boolean().default(true),
    insufficientSkilledLabor: z.boolean().default(true),
    overstaffing: z.boolean().default(true),
    // Compliance
    dailyOvertime: z.boolean().default(true),
    weeklyOvertime: z.boolean().default(true),
    expiredCertifications: z.boolean().default(true),
    untrainedEmployees: z.boolean().default(true),
    // Scheduling
    clopenings: z.boolean().default(true),
    maxConsecutiveWorkdays: z.boolean().default(true),
    insufficientNotice: z.boolean().default(true),
    unavailableEmployees: z.boolean().default(true),
    consecutiveClosingOpening: z.boolean().default(true),
    // Distribution
    unevenDistribution: z.boolean().default(true),
    maxWeekendHours: z.boolean().default(true),
    splitShiftPremium: z.boolean().default(true),
});
// ===== ALERT SETTINGS =====
/**
 * Alert-specific settings for the company
 * Each alert type has its own configuration.
 * When adding a new alert type, add its schema here AND a registry entry
 * in alertSettingsRegistry.ts.
 */
export const AlertsSettingsSchema = z.object({
    checklistOverdue: ChecklistOverdueAlertSettingsSchema,
});
// ===== DOCUMENTS SETTINGS =====
export const DocumentsSettingsSchema = z.object({
    signatureDeadlineDays: z.number().int().min(1).default(7),
});
// ===== MAIN COMPANY SETTINGS SCHEMA =====
export const CompanySettingsSchema = z.object({
    general: GeneralSettingsSchema,
    timeOff: TimeOffSettingsSchema,
    scheduling: SchedulingSettingsSchema,
    clocking: ClockingSettingsSchema,
    pay: PaySettingsSchema,
    checklists: ChecklistSettingsSchema,
    timeAndAttendance: TimeAndAttendanceSettingsSchema,
    tips: TipSettingsSchema,
    career: CareerSettingsSchema,
    reports: ReportSettingsSchema,
    holidaysAndEvents: HolidaysAndEventsSettingsSchema,
    access: AccessSettingsSchema,
    alerts: AlertsSettingsSchema,
    scheduleWarnings: ScheduleWarningTogglesSchema,
    scheduleAlertsDisabledLocationIds: z.array(z.string()).default([]),
    documents: DocumentsSettingsSchema,
});
// ===== DEFAULT SETTINGS =====
export const DEFAULT_COMPANY_SETTINGS = {
    general: {
        timeFormat: "12 hours",
        timeZone: "America/New_York",
        calculateOtHoursAfterWeekly: 40,
        calculateDoubleOtHoursAfterWeekly: null,
        calculateOtHoursAfterDaily: null,
        calculateDoubleOtHoursAfterDaily: null,
        workdayChangeover: "12:00 AM",
        whoCanCreateDistrict: "Admin",
        whoCanCreateStore: ["Admin", "District Manager"],
        districtManagersPerDistrict: 1,
        storeManagersPerDistrict: 1,
        scheduleHoursBeforeOpen: 2,
        scheduleHoursAfterClose: 2,
    },
    timeOff: {
        requireApprovalTeamMember: true,
        requireApprovalSupervisor: true,
        whoApprovesTimeOff: "Your Manager",
        showApprovedTimeOffToAll: true,
        daysNoticeRequired: 14,
        holidayManagement: "Rank Order",
        maxPaidTimeOffHoursPerDay: 8,
        types: {
            paidHoliday: true,
            unpaidHoliday: true,
            paidPersonalDays: true,
            unpaidPersonalDays: true,
            paidSickDays: true,
            unpaidSickDays: true,
        },
        accrualPolicy: "Per pay period",
        accrualRates: "Done by team member, per job",
    },
    scheduling: {
        maxScheduledHoursWeek: 40,
        maxScheduledHoursDay: 8,
        allowWeeklyExceedWithApproval: true,
        allowDailyExceedWithApproval: true,
        notifyDmOtThresholdPercent: 5,
        weekendMaxHoursPerDay: "Same as week day",
        allowBackToBackWeekends: true,
        minHoursPerWeek: 4,
        allowSplitShifts: true,
        minHoursBetweenShiftsSameDay: 1,
        minHoursBetweenShiftsMultiDay: 8,
        maxDaysPerWeek: 7,
        maxDaysScheduledInRow: 9,
        allowShiftRelease: false,
        allowShiftDropping: true,
        shiftDropDaysBefore: 14,
        allowShiftSwapping: true,
        shiftSwapDaysBefore: 1,
        requireManagerApprovalDrops: "Yes",
        requireManagerApprovalSwaps: "Yes",
        minShiftLength: 2,
        maxShiftLength: 8,
        minShiftLengthToSplit: 4,
        minNoticeToChangeShiftHours: 72,
        maxOpeningsPerWeekPerMember: 5,
        maxClosingsPerWeekPerMember: 5,
        allowClopening: true,
        requireShiftConfirmation: true,
        advanceNoticeAvailabilityChangeDays: 21,
        requireManagerApprovalAvailability: true,
        coworkerScheduleVisibility: "Shifts only",
        coworkerBreakVisibility: "Shifts only",
        coworkerInfoVisible: ["Name", "Photo"],
        notifyManagerOtWarning: true,
        notifyManagerOtHoursThreshold: 6,
        enableManagerCostView: true,
        includeSalariedInCosts: true,
        additionalBenefitsPercent: 20,
        laborBudgetPercentOfSales: 0.3,
        highlightNewHires: false,
        unqualifiedJobPayMethod: "Scheduler assigns when publishing schedule",
        breakInScheduledHours: "Do not include employees on break in your scheduled hours",
        includePtoInLaborCost: true,
        alertUnevenHoursDistribution: true,
        maxOpeningShiftsInRow: 7,
        maxClosingShiftsInRow: 7,
        notifyHoursFluctuation: true,
        hoursFluctuationThresholdPercent: 50,
        setMinorThresholds: true,
        minorThresholdRules: "Set by age and day of week",
        shiftIntervals: undefined,
    },
    clocking: {
        clockInMethods: ["Phone", "Shared iPad"],
        requireGeofencing: true,
        geofenceDistanceFt: 500,
        requirePhotoSharedDevice: true,
        earlyClockInMinutesEmployee: 5,
        earlyClockInMinutesSupervisor: 15,
        unplannedWorkPolicy: "With shift supervisor approval",
        whoCanClockOutEmployee: ["Manager", "Shift Supervisor", "District Manager"],
        notifyEmployeeBeforeClockIn: null,
        notifyEmployeeAfterClockInMinutes: 5,
        notifyManagerAfterClockInMinutes: 5,
        notifyEmployeeAfterClockOutMinutes: 5,
        notifyManagerAfterClockOutMinutes: 10,
        roundClockInOut: null,
        requireClockUnpaidBreaks: true,
        requireClockPaidBreaks: true,
        askBreakAttestationOnClockOut: true,
        requireSalariedClockInOut: false,
        requirePinSharedDevice: true,
        pinLength: 4,
        shiftChangeRequestThresholdMinutes: 15,
        supervisorJobRoleFamilyIds: [],
    },
    pay: {
        currentPayPeriodStart: null,
        payPeriodLength: "Every 2 weeks",
        allowPayOnDemand: false,
        payOnDemandEarliestRequest: "Day of shift",
        whoApprovesCompensationChanges: "Skip level manager",
        payrollProvider: "default",
    },
    checklists: {
        useChecklists: true,
        allowPartialAssignment: true,
    },
    timeAndAttendance: {
        notifyManagerHoursThreshold: 45,
        notifyManagerDaysWorkedThreshold: 6,
        notifyManagerInactiveDays: 21,
        notifyManagerBeforeAdditionalPay: true,
        notifyManagerAfterAdditionalPay: true,
        notifyManagerMissedBreaks: true,
        notifyManagerUnplannedWork: true,
        notifyManagerUnapprovedHours: true,
        notifyManagerUnsubmittedHours: true,
        requireTimecardSubmission: true,
        requireTimecardAttestation: true,
        requireManagerTimecardApproval: true,
        allowTimecardAutoApproval: true,
        autoApproveThresholdMinutes: 5,
        whoApprovesTimecards: "APPROVER_GROUP",
        backupTimecardApprovers: ["Manager"],
        timecardApprovalDeadlineDays: 3,
        timecardApprovalDeadlineTime: "12:00 PM",
        enableAttendancePoints: true,
        oooApproverPolicy: ["Assign before going out of office", "manager"],
    },
    tips: {
        requireManagerApprovalTips: true,
        tipDistributionUnsubmitted: "Scheduled",
        externalTipSources: [],
        pennyRoundingStrategy: "lowest_earner",
        timecardStateHandling: "fallback_to_scheduled",
    },
    career: {
        reviewCycleFrequency: "Twice a year",
        batchReviewCycles: true,
        batchReviewFrequency: "Monthly",
        enableTriggeredPromotionReviews: true,
    },
    reports: {
        newReportNotification: ["App notification", "to do list"],
        publicReportsAvailable: "Detailed",
        reportDeliveryCadence: "Detailed",
        reportDeliveryTimeUtc: "08:00",
        reportDeliveryDayOfWeek: "MONDAY",
        reportDeliveryWeekOccurrence: "FIRST",
    },
    holidaysAndEvents: {
        whoCanAddEvent: ["Location Manager", "District Manager", "Admin"],
        whoApprovesAddingEvent: "District Manager",
    },
    access: {
        enableExternalPartners: true,
    },
    alerts: {
        checklistOverdue: {
            enabled: true,
            warningDaysOverdue: 1,
            criticalDaysOverdue: 4,
            checkIntervalSeconds: 3600, // 1 hour
        },
    },
    scheduleWarnings: {
        overlappingShifts: true,
        insufficientLabor: true,
        insufficientSkilledLabor: true,
        overstaffing: true,
        dailyOvertime: true,
        weeklyOvertime: true,
        expiredCertifications: true,
        untrainedEmployees: true,
        clopenings: true,
        maxConsecutiveWorkdays: true,
        insufficientNotice: true,
        unavailableEmployees: true,
        consecutiveClosingOpening: true,
        unevenDistribution: true,
        maxWeekendHours: true,
        splitShiftPremium: true,
    },
    scheduleAlertsDisabledLocationIds: [],
    documents: {
        signatureDeadlineDays: 7,
    },
};
